import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Clapperboard, Send } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  {
    icon: Sparkles,
    title: "Tell us your topic",
    text: "Type any idea — AI writes the full script, hook and captions for a faceless video in seconds.",
  },
  {
    icon: Clapperboard,
    title: "AI builds your video",
    text: "Pick a visual style and voice. We generate scenes, voiceover and visuals — add 3D motion for extra punch.",
  },
  {
    icon: Send,
    title: "Publish & grow",
    text: "Download, publish straight to YouTube, or schedule a whole series to post automatically while you sleep.",
  },
];

export const WelcomeTour = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const show = !!user && user.auth_provider === "google" && user.tour_seen === false;
  if (!show) return null;

  const markSeen = () => {
    updateUser({ tour_seen: true });
    api.post("/auth/tour-seen").catch(() => {});
  };
  const isLast = step === STEPS.length - 1;
  const { icon: Icon, title, text } = STEPS[step];

  return (
    <Dialog open onOpenChange={(o) => !o && markSeen()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md" data-testid="welcome-tour">
        <div className="pt-2 pb-1 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-4">
            Welcome{user.name ? `, ${user.name.split(" ")[0]}` : ""} 👋
          </p>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center mb-4">
            <Icon className="h-7 w-7 text-blue-400" />
          </div>
          <DialogTitle className="font-head text-xl font-bold mb-2 text-center" data-testid="welcome-tour-title">{title}</DialogTitle>
          <DialogDescription className="text-sm text-zinc-400 mb-6 max-w-xs mx-auto">{text}</DialogDescription>
          <div className="flex items-center justify-center gap-2 mb-6" data-testid="welcome-tour-dots">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? "w-6 bg-blue-500" : "w-1.5 bg-white/20"}`} />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={markSeen}
              data-testid="welcome-tour-skip"
              className="flex-1 py-2.5 rounded-md text-sm text-zinc-500 hover:text-white border border-white/10 hover:bg-white/5 transition-colors"
            >
              Skip
            </button>
            {isLast ? (
              <button
                onClick={() => { markSeen(); navigate("/create"); }}
                data-testid="welcome-tour-cta"
                className="flex-[2] py-2.5 rounded-md text-sm font-semibold bg-blue-600 hover:bg-blue-500 transition-colors"
              >
                Create my first video
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => s + 1)}
                data-testid="welcome-tour-next"
                className="flex-[2] py-2.5 rounded-md text-sm font-semibold bg-blue-600 hover:bg-blue-500 transition-colors"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
